import {
  Box,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { t } from 'i18next';
import { Button, Typography } from 'app/shared';
import { IExtraSubstanceOutcome, IReportSchema, IReportUpdate, ISubstanceOutcome } from 'generated/axios';
import { useState } from 'react';
import { isEmpty } from 'lodash';
import { getSubstanceLabel, getSubstanceOutcomeLabel, renderSubstanceOutcomeIcon } from './substances';
import { FormHook } from 'app/hooks';

interface Props {
  detail: IReportSchema | undefined;
  updateForm: FormHook<IReportUpdate>;
  loading: boolean;
}

export const ReportResultColumn = ({ detail, updateForm, loading }: Props) => {
  //change results manually
  const [resultReadOnly, setResultReadOnly] = useState<boolean>(true);
  const toggleResultReadOnly = () => setResultReadOnly((prev) => !prev);
  const { bindSelect, submit } = updateForm;

  return detail && isEmpty(detail?.test_result) ? (
    <label>{t('report.testResultNotAvailable')}</label>
  ) : (
    <>
      <Box textAlign={'center'}>
        {resultReadOnly ? (
          <Button loading={loading} onClick={toggleResultReadOnly}>
            {t('report.updateResults')}
          </Button>
        ) : (
          <>
            <Button
              loading={loading}
              color="warning"
              onClick={() => {
                submit();
                toggleResultReadOnly();
              }}
            >
              {t('actions.submit')}
            </Button>
            <Button loading={loading} onClick={toggleResultReadOnly}>
              {t('actions.cancel')}
            </Button>
          </>
        )}
      </Box>
      <TableContainer sx={{ display: 'flex', justifyContent: 'center', overflowX: 'hidden' }}>
        <Table sx={{ width: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell width={300}>
                <Typography>{t('common.name')}</Typography>
              </TableCell>
              <TableCell width={100}>
                <Typography>{t('report.result')}</Typography>
              </TableCell>
              {!resultReadOnly && <TableCell width={200}></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {detail?.test_result?.substances?.map((substance, idx) => (
              <TableRow>
                <TableCell>
                  <Typography variant="body2">{getSubstanceLabel(substance.name)}</Typography>
                </TableCell>
                <TableCell>
                  <Box textAlign={'center'} display={'flex'}>
                    {renderSubstanceOutcomeIcon(substance.outcome)}
                    <Typography variant="subtitle2">{getSubstanceOutcomeLabel(substance.outcome)}</Typography>
                  </Box>
                </TableCell>
                {!resultReadOnly && (
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      {...bindSelect({ field: 'test_result', path: ['substances', idx.toString(), 'outcome'] })}
                    >
                      <MenuItem value={ISubstanceOutcome.Unset}>&nbsp;</MenuItem>
                      {Object.entries(ISubstanceOutcome)
                        .filter(([_key, value]) => value !== ISubstanceOutcome.Unset)
                        .map(([key, value]) => (
                          <MenuItem key={key} value={value}>
                            {getSubstanceOutcomeLabel(value)}
                          </MenuItem>
                        ))}
                    </TextField>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {detail?.test_result?.extra_substances?.map((substance, idx) => (
              <TableRow>
                <TableCell>
                  <Typography variant="body2">{getSubstanceLabel(substance.name)}</Typography>
                </TableCell>
                <TableCell>
                  <Box textAlign={'center'} display={'flex'}>
                    {renderSubstanceOutcomeIcon(substance.outcome)}
                    <Typography variant="subtitle2">{getSubstanceOutcomeLabel(substance.outcome)}</Typography>
                  </Box>
                </TableCell>
                {!resultReadOnly && (
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      {...bindSelect({ field: 'test_result', path: ['extra_substances', idx.toString(), 'outcome'] })}
                    >
                      <MenuItem value={IExtraSubstanceOutcome.Unset}>&nbsp;</MenuItem>
                      {Object.entries(IExtraSubstanceOutcome)
                        .filter(([_key, value]) => value !== IExtraSubstanceOutcome.Unset)
                        .map(([key, value]) => (
                          <MenuItem key={key} value={value}>
                            {getSubstanceOutcomeLabel(value)}
                          </MenuItem>
                        ))}
                    </TextField>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
